import React, { useState } from 'react';
import { Table, Tag, Input, Select, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getItemUsageDetails, getImageUrl } from './javamonUtils';

interface RewardsUsageViewProps {
    type: string;
    items: any[];
    rewardsUsage: any;
    loading: boolean;
    loadingRewardsUsage: boolean;
    fetchRewardsUsage: () => Promise<void>;
}

export const RewardsUsageView: React.FC<RewardsUsageViewProps> = ({
    type,
    items,
    rewardsUsage,
    loading,
    loadingRewardsUsage,
    fetchRewardsUsage
}) => {
    const [usageFilterStatus, setUsageFilterStatus] = useState<string>('all');
    const [usageSearchText, setUsageSearchText] = useState<string>('');

    const enrichedItems = items.map((it: any) => {
        const usage = getItemUsageDetails(type, it, rewardsUsage);
        return {
            ...it,
            _usage: usage
        };
    });

    const filtered = enrichedItems.filter((it: any) => {
        if (usageFilterStatus === 'obtainable' && !it._usage.isObtainable) return false;
        if (usageFilterStatus === 'unobtainable' && it._usage.isObtainable) return false;
        if (usageSearchText) {
            const q = usageSearchText.toLowerCase();
            const matchNom = (it.nom || '').toLowerCase().includes(q);
            const matchIdent = (it.ident || '').toLowerCase().includes(q);
            const matchSources =
                it._usage.pildoras.some((p: any) => p.nombre.toLowerCase().includes(q) || p.conjunt_nom.toLowerCase().includes(q)) ||
                it._usage.actividades.some((a: any) => a.nom.toLowerCase().includes(q) || a.problema_nom.toLowerCase().includes(q)) ||
                it._usage.practicas.some((pr: any) => pr.nom.toLowerCase().includes(q) || pr.problema_nom.toLowerCase().includes(q)) ||
                it._usage.shopItems.some((s: any) => s.nom.toLowerCase().includes(q)) ||
                it._usage.botinesList.some((b: any) => b.botin_nom.toLowerCase().includes(q));
            if (!matchNom && !matchIdent && !matchSources) return false;
        }
        return true;
    });

    const totalObtainable = enrichedItems.filter((it: any) => it._usage.isObtainable).length;
    const totalUnobtainable = enrichedItems.length - totalObtainable;

    const columns = [
        {
            title: 'Imagen',
            key: 'imagen',
            width: 70,
            render: (_: any, record: any) => (
                <img
                    src={getImageUrl(record, type)}
                    alt={record.nom}
                    style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5', padding: 2 }}
                />
            )
        },
        {
            title: 'Nombre / Ident',
            key: 'nombre',
            width: 180,
            render: (_: any, record: any) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{record.nom}</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        ID: {record.id} {record.ident ? `| ${record.ident}` : ''}
                    </div>
                    {record.tier && <Tag color="blue" style={{ marginTop: 2, fontSize: 10 }}>Tier: {record.tier}</Tag>}
                    {record.type && type === 'skin' && <Tag color="geekblue" style={{ marginTop: 2, fontSize: 10 }}>{record.type}</Tag>}
                </div>
            )
        },
        {
            title: 'Estado Recompensa',
            key: 'estado',
            width: 140,
            render: (_: any, record: any) => {
                const u = record._usage;
                if (u.isObtainable) {
                    return (
                        <div>
                            <Tag color="success" style={{ fontWeight: 600, padding: '2px 8px' }}>
                                ✓ Obtenible ({u.totalSources})
                            </Tag>
                        </div>
                    );
                }
                return (
                    <div>
                        <Tag color="error" style={{ padding: '2px 8px' }}>
                            ✕ Sin recompensa
                        </Tag>
                    </div>
                );
            }
        },
        {
            title: '🍬 Píldoras / Caza',
            key: 'pildoras',
            render: (_: any, record: any) => {
                const list = record._usage.pildoras;
                if (list.length === 0) return <span style={{ color: '#bfbfbf', fontSize: 12 }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {list.map((p: any, idx: number) => (
                            <Tag key={idx} color="orange" style={{ margin: 0, whiteSpace: 'normal', height: 'auto', padding: '3px 8px' }}>
                                🎯 <b>{p.nombre}</b>
                                {p.conjunt_nom && <span style={{ color: '#595959', fontSize: 11 }}> ({p.conjunt_nom})</span>}
                                {p.aula_nom && <span style={{ color: '#8c8c8c', fontSize: 10 }}> [{p.aula_nom}]</span>}
                            </Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: '📝 Actividades',
            key: 'actividades',
            render: (_: any, record: any) => {
                const list = record._usage.actividades;
                if (list.length === 0) return <span style={{ color: '#bfbfbf', fontSize: 12 }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {list.map((a: any, idx: number) => (
                            <Tag key={idx} color="cyan" style={{ margin: 0, whiteSpace: 'normal', height: 'auto', padding: '3px 8px' }}>
                                📄 <b>{a.nom}</b>: {a.problema_nom}
                                {a.aula_nom && <span style={{ color: '#8c8c8c', fontSize: 10 }}> [{a.aula_nom}]</span>}
                            </Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: '🚀 Prácticas / Proyectos',
            key: 'practicas',
            render: (_: any, record: any) => {
                const list = record._usage.practicas;
                if (list.length === 0) return <span style={{ color: '#bfbfbf', fontSize: 12 }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {list.map((pr: any, idx: number) => {
                            const label = pr.origen_tipo === 'PRACTICA'
                                ? <b>{pr.nom}</b>
                                : <><b>{pr.nom}</b>: {pr.problema_nom}</>;
                            return (
                                <Tag key={idx} color="purple" style={{ margin: 0, whiteSpace: 'normal', height: 'auto', padding: '3px 8px' }}>
                                    🏆 {label}
                                    {pr.aula_nom && <span style={{ color: '#8c8c8c', fontSize: 10 }}> [{pr.aula_nom}]</span>}
                                </Tag>
                            );
                        })}
                    </div>
                );
            }
        },
        {
            title: '🛒 Tienda',
            key: 'tienda',
            render: (_: any, record: any) => {
                const list = record._usage.shopItems;
                if (list.length === 0) return <span style={{ color: '#bfbfbf', fontSize: 12 }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {list.map((s: any, idx: number) => (
                            <Tag key={idx} color="gold" style={{ margin: 0, whiteSpace: 'normal', height: 'auto', padding: '3px 8px' }}>
                                💰 <b>{s.nom}</b> ({s.cost} pts)
                                {s.stock !== -1 && <span style={{ fontSize: 11 }}> [Stock: {s.stock}]</span>}
                            </Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: '📦 Botines (Loot)',
            key: 'botines',
            render: (_: any, record: any) => {
                const list = record._usage.botinesList;
                if (list.length === 0) return <span style={{ color: '#bfbfbf', fontSize: 12 }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {list.map((b: any, idx: number) => (
                            <Tag key={idx} color="magenta" style={{ margin: 0, whiteSpace: 'normal', height: 'auto', padding: '3px 8px' }}>
                                🎁 <b>{b.botin_nom}</b> ({b.botin_identifier})
                            </Tag>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                padding: '12px 16px',
                background: '#f8f9fa',
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                marginBottom: 16
            }}>
                <Space size="middle" wrap>
                    <div>
                        <b>Total:</b> <Tag color="default">{enrichedItems.length}</Tag>
                    </div>
                    <div>
                        <b>Se consiguen como recompensa:</b> <Tag color="green">{totalObtainable}</Tag>
                    </div>
                    <div>
                        <b>Sin recompensa asignada:</b> <Tag color="volcano">{totalUnobtainable}</Tag>
                    </div>
                </Space>

                <Space wrap>
                    <Input.Search
                        placeholder="Buscar por nombre o lugar..."
                        value={usageSearchText}
                        onChange={(e) => setUsageSearchText(e.target.value)}
                        onSearch={(v) => setUsageSearchText(v)}
                        style={{ width: 240 }}
                        allowClear
                    />
                    <Select
                        value={usageFilterStatus}
                        onChange={(v) => setUsageFilterStatus(v)}
                        style={{ width: 190 }}
                        options={[
                            { value: 'all', label: '🔍 Todos los elementos' },
                            { value: 'obtainable', label: '✅ Solo obtenibles' },
                            { value: 'unobtainable', label: '⚠️ Solo no obtenibles' }
                        ]}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchRewardsUsage}
                        loading={loadingRewardsUsage}
                    >
                        Actualizar recompensas
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={filtered}
                columns={columns}
                rowKey="id"
                loading={loading || loadingRewardsUsage}
                pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '30', '50'] }}
                bordered
                size="small"
            />
        </div>
    );
};
